import { Search, X, Loader2 } from 'lucide-react';
import {
  forwardRef,
  InputHTMLAttributes,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { searchInputVariants } from '@/shared/constants/ui-variants';
import { cn } from '@/shared/lib/utils';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import { Button } from './button';

export interface SearchInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'type' | 'onSearch' | 'size'
> {
  readonly onSearch: (value: string) => void;
  readonly debounceMs?: number | undefined;
  readonly loading?: boolean | undefined;
  readonly onClear?: (() => void) | undefined;
  readonly showClearButton?: boolean | undefined;
  readonly searchOnEnter?: boolean | undefined;
}

/**
 * SearchInput component with debouncing and clear functionality
 *
 * Features:
 * - Debounced search input for performance
 * - Clear button (X)
 * - Loading indicator
 * - Mobile keyboard optimization (inputMode="search")
 * - WCAG 2.1 AA compliant
 *
 * @example
 * ```tsx
 * <SearchInput
 *   placeholder="Search users..."
 *   onSearch={handleSearch}
 *   debounceMs={300}
 *   loading={isLoading}
 * />
 * ```
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      onSearch,
      debounceMs = 300,
      loading = false,
      onClear,
      showClearButton = true,
      searchOnEnter = false,
      placeholder = 'Search...',
      disabled = false,
      value: controlledValue,
      ...props
    },
    ref
  ) => {
    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState(
      isControlled ? String(controlledValue) : ''
    );

    // Mirror an external controlled value into local state so programmatic
    // changes (e.g. "clear all") are reflected while typing stays debounced.
    useEffect(() => {
      if (isControlled) {
        setInternalValue(String(controlledValue));
      }
    }, [controlledValue, isControlled]);

    const value = internalValue;

    // Debounce search callback
    useEffect(() => {
      if (searchOnEnter) return undefined; // Don't debounce if searching on Enter only

      const handler = setTimeout(() => {
        onSearch(String(value));
      }, debounceMs);

      return () => {
        clearTimeout(handler);
      };
    }, [value, debounceMs, onSearch, searchOnEnter]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setInternalValue(event.target.value);
      },
      []
    );

    const handleClear = useCallback(() => {
      setInternalValue('');
      onSearch('');
      onClear?.();
    }, [onSearch, onClear]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (searchOnEnter && event.key === 'Enter') {
          event.preventDefault();
          onSearch(String(value));
        }
      },
      [searchOnEnter, value, onSearch]
    );

    const hasValue = String(value).length > 0;

    return (
      <div className={cn('relative w-full', className)}>
        {/* Search icon */}
        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
          <Search className="size-4 text-muted-foreground" aria-hidden="true" />
        </div>

        {/* Input field */}
        <input
          ref={ref}
          type="search"
          inputMode="search"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || loading}
          className={cn(searchInputVariants(), 'pl-10', loading && 'pr-24')}
          aria-label={typeof placeholder === 'string' ? placeholder : 'Search'}
          {...props}
        />

        {/* Right side controls */}
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {/* Loading indicator */}
          {loading && (
            <div
              className="flex size-7 items-center justify-center"
              role="status"
              aria-live="polite"
              aria-label="Searching"
            >
              <Loader2
                className="size-4 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
            </div>
          )}

          {/* Clear button */}
          {showClearButton && hasValue && !loading && (
            <Button
              type="button"
              variant={ButtonVariant.Ghost}
              size={ButtonSize.Small}
              onClick={handleClear}
              disabled={disabled}
              className="size-7 min-h-[28px] min-w-[28px] p-0 hover:bg-accent"
              aria-label="Clear search"
            >
              <X className="size-4 text-muted-foreground" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
